import * as React from 'react';
import type { ReactNode } from 'react';
import { cx } from '../utils';
import type { StyledProps } from '../types';
import { tintVar } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';
import { devWarn, IS_PROD } from '../internal/devWarn';

export type EmptyTone = 'neutral' | 'info' | 'error';
export type EmptyTitleAs = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div';

export interface EmptyProps extends StyledProps, UnsafeStyleProps {
  /* Optional leading visual — `<JIcon>`, an `<img>`, or a custom SVG.
     Adopters mark decorative icons with `aria-hidden`. */
  icon?: ReactNode;
  /* Required heading content. Rendered inside the element chosen by
     `titleAs` (default `<h3>`). */
  title: ReactNode;
  /* Optional secondary line. */
  description?: ReactNode;
  /* Optional trailing action cluster — typically one or two `<Button>`s
     composed by the adopter (`<Stack horizontal>` if a specific layout
     is needed). */
  actions?: ReactNode;
  /* @deprecated — v4 alias for `description`. Emits a one-shot dev
     warning; removed in v6. */
  hint?: ReactNode;
  /* Heading depth. Default `h3` is the common case (page or section
     contains an `h2`; Empty sits underneath). Set `'div'` only when
     the surrounding context already provides a heading (e.g., inside
     a `<Modal>` titled by the modal heading). */
  titleAs?: EmptyTitleAs;
  /* Visual tone — drives the title color tint. Default `neutral` is
     the body color; `info` uses theme; `error` uses error. */
  tone?: EmptyTone;
}

/* Dedupe scope for the `hint` deprecation warning: module-level so
   apps with many <Empty> instances don't get spammed. */
const _hintWarned = { fired: false };

export const Empty = React.forwardRef<HTMLDivElement, EmptyProps>(function Empty(
  {
    icon,
    title,
    description,
    actions,
    hint,
    titleAs = 'h3',
    tone = 'neutral',
    className,
    unsafe_style,
  },
  ref,
) {
  if (!IS_PROD && hint !== undefined && !_hintWarned.fired) {
    _hintWarned.fired = true;
    devWarn(
      '[JKit.Empty] `hint` is deprecated — rename to `description`. ' +
        'Removal scheduled for v6. (This warning fires once per session.)',
    );
  }

  /* `description` takes precedence over the deprecated `hint`,
     including the case where `description={null}` — a caller passing
     null is *intentionally* suppressing the secondary line during
     migration; `??` would incorrectly fall through to hint in that
     case (codex round 2). The fallback only fires when `description`
     was never supplied at all (i.e., `description === undefined`). */
  const descNode = description !== undefined ? description : hint;
  /* When the description came in through the deprecated `hint` prop
     AND the adopter has not also provided `description`, emit the
     legacy `.j-empty-hint` class alongside `.j-empty-description` so
     v4 custom CSS targeting `.j-empty-hint` keeps applying during
     the deprecation window. Once the adopter migrates to
     `description`, the legacy class drops off naturally (codex
     round 1). */
  const descClassName =
    description === undefined && hint !== undefined
      ? 'j-empty-description j-empty-hint'
      : 'j-empty-description';

  /* Tone tint applies to the heading; for neutral we leave the color
     to the cascade (--fg-1). tintVar returns undefined for an unknown
     tint, falling through to the CSS default. */
  const titleColor =
    tone === 'neutral'
      ? undefined
      : tone === 'info'
        ? tintVar('theme')
        : /* error */ tintVar('error');

  return (
    <div ref={ref} className={cx('j-empty', className)} style={unsafe_style} data-tone={tone}>
      {icon != null && icon !== false && (
        <span className="j-empty-icon" data-tone={tone}>
          {icon}
        </span>
      )}
      {React.createElement(
        titleAs,
        {
          className: 'j-empty-title',
          style: titleColor ? { color: titleColor } : undefined,
        },
        title,
      )}
      {descNode != null && descNode !== false && <div className={descClassName}>{descNode}</div>}
      {actions != null && actions !== false && <div className="j-empty-actions">{actions}</div>}
    </div>
  );
});
